import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

function getLocalDate() {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getDateLabel() {
  const d = new Date()
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

const PROMPTS = [
  { key: 'best_part',       label: 'Best part of today',       placeholder: 'What went well?' },
  { key: 'worst_part',      label: 'Worst part of today',      placeholder: 'What was challenging?' },
  { key: 'spent_time_with', label: 'Spent the most time with', placeholder: 'Who did you spend time with?' },
  { key: 'tomorrow_goal',   label: "Tomorrow's #1 goal",       placeholder: 'What must get done tomorrow?' },
]

export default function Journal({ userId }) {
  const [entry, setEntry] = useState({
    best_part: '', worst_part: '', spent_time_with: '',
    tomorrow_goal: '', free_write: '', day_rating: null,
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const autoSaveTimer = React.useRef(null)

  // Load today's entry on mount
  useEffect(() => {
    if (userId) loadEntry()
  }, [userId])

  async function loadEntry() {
    const { data } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', userId)
      .eq('date', getLocalDate())
      .single()

    if (data) {
      setEntry({
        best_part:        data.best_part || '',
        worst_part:       data.worst_part || '',
        spent_time_with:  data.spent_time_with || '',
        tomorrow_goal:    data.tomorrow_goal || '',
        free_write:       data.free_write || '',
        day_rating:       data.day_rating || null,
      })
    }
    setLoaded(true)
  }

  function handleChange(key, value) {
    const updated = { ...entry, [key]: value }
    setEntry(updated)
    // Auto-save after 1.5 seconds of no typing
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(() => saveEntry(updated), 1500)
  }

  async function saveEntry(updatedEntry) {
    const toSave = updatedEntry || entry
    setSaving(true)
    const today = getLocalDate()

    // Check if entry exists for today
    const { data: existing } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('user_id', userId)
      .eq('date', today)
      .single()

    if (existing) {
      // Update existing
      await supabase
        .from('journal_entries')
        .update({ ...toSave, updated_at: new Date().toISOString() })
        .eq('id', existing.id)
    } else {
      // Insert new
      await supabase
        .from('journal_entries')
        .insert({ user_id: userId, date: today, ...toSave })
    }

    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  if (!loaded) return <div style={{ color: '#6b6860', textAlign: 'center', padding: '40px 0' }}>Loading...</div>

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.dateLabel}>{getDateLabel()}</div>
        </div>
        <div style={s.headerRight}>
          {saving && <span style={s.savingText}>Saving...</span>}
          {saved && !saving && <span style={s.savedText}>Saved ✓</span>}
          {entry.day_rating && <div style={s.ratingBadge}>{entry.day_rating}/10</div>}
        </div>
      </div>

      <div style={s.body}>
        {PROMPTS.map(p => (
          <div key={p.key} style={s.promptRow}>
            <div style={s.promptLabel}>{p.label}</div>
            <textarea
              style={s.textarea}
              placeholder={p.placeholder}
              value={entry[p.key]}
              onChange={e => handleChange(p.key, e.target.value)}
              rows={2}
            />
          </div>
        ))}

        <div style={s.promptRow}>
          <div style={s.promptLabel}>Free write</div>
          <textarea
            style={{ ...s.textarea, minHeight: '80px' }}
            placeholder="Anything else on your mind..."
            value={entry.free_write}
            onChange={e => handleChange('free_write', e.target.value)}
            rows={3}
          />
        </div>

        <div style={s.promptRow}>
          <div style={s.promptLabel}>Rate your day</div>
          <div style={s.ratingRow}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <div key={n}
                style={{
                  ...s.ratingDot,
                  background: entry.day_rating >= n
                    ? n >= 8 ? '#4caf7d' : n >= 5 ? '#c9a84c' : '#e05c5c'
                    : 'rgba(255,255,255,0.12)',
                  transform: entry.day_rating === n ? 'scale(1.15)' : 'scale(1)',
                  color: entry.day_rating >= n ? '#000' : '#6b6860',
                }}
                onClick={() => {
                  const updated = { ...entry, day_rating: n }
                  setEntry(updated)
                  saveEntry(updated)
                }}>
                {n}
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} onClick={() => saveEntry(entry)}>
          {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save Journal'}
        </div>
      </div>
    </div>
  )
}

const s = {
  wrap: { background: 'transparent' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 0 16px 0' },
  headerLeft: { flex: 1 },
  dateLabel: { fontSize: '13px', color: '#6b6860' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  savingText: { fontSize: '11px', color: '#6b6860' },
  savedText: { fontSize: '11px', color: '#4caf7d' },
  ratingBadge: { background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c', fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '6px' },
  body: { display: 'flex', flexDirection: 'column', gap: '0' },
  promptRow: { marginBottom: '16px' },
  promptLabel: { fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '8px' },
  textarea: { width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', padding: '12px 14px', color: '#e8e6df', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', resize: 'none', lineHeight: '1.5', boxSizing: 'border-box', WebkitAppearance: 'none' },
  ratingRow: { display: 'flex', gap: '5px' },
  ratingDot: { flex: 1, aspectRatio: '1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' },
  saveBtn: { marginTop: '8px', padding: '14px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', fontSize: '15px', fontWeight: '700', fontFamily: "'DM Sans', sans-serif", transition: 'opacity 0.2s' },
}
